import { useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Upload, X, FileImage, Video, Map, Building2, Layers3, Eye,
  Link2, Plus, Trash2, AlertCircle, CheckCircle, Loader2, Image as ImageIcon,
  ExternalLink, Youtube, Maximize2, FolderUp, Bookmark, Save
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MEDIA_CATEGORIES = [
  { key: "floor_plan", label: "Floor Plan", icon: FileImage, accept: "image/*,.pdf", description: "Upload floor plan images or PDFs" },
  { key: "project_plan", label: "Project Plan", icon: Building2, accept: "image/*,.pdf", description: "Project layout & master plan" },
  { key: "location_map", label: "Location Map", icon: Map, accept: "image/*,.pdf", description: "Site/location map" },
  { key: "room_floor_plan", label: "Room Floor Plan", icon: Layers3, accept: "image/*,.pdf", description: "Detailed room layouts" },
  { key: "three_d_view", label: "3D View", icon: Eye, accept: "image/*,.pdf", description: "3D renderings & perspectives" },
  { key: "images", label: "Property Images", icon: ImageIcon, accept: "image/*", description: "Photos of the property" },
  { key: "videos", label: "Videos", icon: Video, accept: "video/*", description: "Property walkthrough videos" },
];

const FIELD_MAP = {
  floor_plan: "floor_plan_files",
  project_plan: "project_plan_files",
  location_map: "location_map_files",
  room_floor_plan: "room_floor_plan_files",
  three_d_view: "three_d_view_files",
  images: "image_files",
  videos: "video_files",
};

// Legacy field map for backward compatibility with old single-file uploads
const LEGACY_FIELD_MAP = {
  floor_plan: "floor_plan_image",
  project_plan: "project_plan_image",
  location_map: "location_image",
  room_floor_plan: "room_floor_plan_image",
  three_d_view: "three_d_image",
};

function formatFileSize(bytes) {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getStorageFileUrl(storagePath, token) {
  return `${API}/storage/files/${storagePath}?auth=${token}`;
}

function isImageFile(file) {
  const ext = (file.original_filename || file.name || "").split(".").pop().toLowerCase();
  return ["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(ext);
}

function isVideoFile(file) {
  const ext = (file.original_filename || file.name || "").split(".").pop().toLowerCase();
  return ["mp4", "mov", "webm", "avi", "mkv"].includes(ext);
}

/** Single media category card with upload, preview, delete */
function CategoryCard({ category, propertyId, existingFiles, legacyUrl, pendingFiles, onPendingAdd, onPendingRemove, onFileUploaded, onFileDeleted, isEditMode }) {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const token = localStorage.getItem("token");

  const processFiles = useCallback(async (selectedFiles) => {
    if (!selectedFiles.length) return;

    if (isEditMode && propertyId) {
      setUploading(true);
      const formData = new FormData();
      formData.append("category", category.key);
      selectedFiles.forEach(f => formData.append("files", f));

      try {
        const res = await axios.post(`${API}/properties/${propertyId}/media-upload`, formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
        const uploaded = res.data.files || [];
        const compressed = res.data.compressed || [];
        if (compressed.length > 0) {
          toast.success(`${compressed.length} file(s) auto-compressed to under 4.9 MB`);
        }
        toast.success(`${uploaded.length} file(s) uploaded to ${category.label}`);
        onFileUploaded(category.key, uploaded);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Upload failed");
      } finally {
        setUploading(false);
      }
    } else {
      selectedFiles.forEach(f => onPendingAdd(category.key, f));
    }
  }, [category.key, propertyId, isEditMode, token, onFileUploaded, onPendingAdd, category.label]);

  const handleFileSelect = useCallback(async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    await processFiles(selectedFiles);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [processFiles]);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files || []);
    await processFiles(droppedFiles);
  }, [processFiles]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDelete = useCallback(async (file, index) => {
    if (isEditMode && propertyId && file.storage_path) {
      setDeleting(index);
      try {
        await axios.delete(`${API}/properties/${propertyId}/media-file`, {
          params: { category: category.key, storage_path: file.storage_path },
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("File removed");
        onFileDeleted(category.key, file.storage_path);
      } catch (err) {
        toast.error("Failed to remove file");
      } finally {
        setDeleting(null);
      }
    }
  }, [category.key, propertyId, isEditMode, token, onFileDeleted]);

  // Merge existing stored files + legacy Cloudinary URL
  const allFiles = [...(existingFiles || [])];
  if (legacyUrl && !allFiles.find(f => f.storage_path === legacyUrl)) {
    allFiles.unshift({ storage_path: legacyUrl, original_filename: "Legacy upload", content_type: "image/jpeg", size: 0, isLegacy: true });
  }

  const totalCount = allFiles.length + (pendingFiles || []).length;

  return (
    <div
      className={`border rounded-lg overflow-hidden transition-colors ${
        dragOver ? "border-[#c9a227] border-2 bg-[#fffdf5]" : "border-slate-200 hover:border-[#c9a227]/50"
      }`}
      data-testid={`media-category-${category.key}`}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >  {/* Header */}
      <div className="bg-gradient-to-r from-slate-50 to-[#f8f6f0] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <category.icon className="h-5 w-5 text-[#0f2744]" />
          <div>
            <h4 className="text-sm font-semibold text-[#0f2744]">{category.label}</h4>
            <p className="text-xs text-slate-500">{category.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs bg-[#0f2744]/10 text-[#0f2744] px-2 py-0.5 rounded-full font-medium">
            {totalCount} file{totalCount !== 1 ? "s" : ""}
          </span>
          <label className="cursor-pointer">
            <input
              ref={fileInputRef}
              type="file"
              accept={category.accept}
              multiple
              onChange={handleFileSelect}
              className="hidden"
              data-testid={`upload-${category.key}-input`}
            />
            <span className="inline-flex items-center gap-1 bg-[#c9a227] text-white px-3 py-1.5 rounded text-xs font-medium hover:bg-[#b8911f] transition-colors">
              {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
              {uploading ? "Uploading..." : "Add Files"}
            </span>
          </label>
        </div>
      </div>

      {/* File Grid */}
      {totalCount > 0 ? (
        <div className="p-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {/* Existing stored files */}
          {allFiles.map((file, idx) => {
            const url = file.isLegacy ? file.storage_path : getStorageFileUrl(file.storage_path, token);
            const isImage = file.isLegacy || isImageFile(file);
            const isVideo = isVideoFile(file);

            return (
              <div key={`stored-${idx}`} className="relative group aspect-square rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
                {isImage ? (
                  <img src={url} alt={file.original_filename} className="w-full h-full object-cover" loading="lazy" />
                ) : isVideo ? (
                  <div className="w-full h-full flex items-center justify-center bg-slate-900">
                    <Video className="h-8 w-8 text-white/50" />
                    <span className="absolute bottom-1 left-1 text-[10px] text-white/70 truncate max-w-[90%]">{file.original_filename}</span>
                  </div>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center p-2">
                    <FileImage className="h-8 w-8 text-slate-400" />
                    <span className="text-[10px] text-slate-500 mt-1 truncate max-w-full">{file.original_filename}</span>
                  </div>
                )}

                {/* Overlay actions */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                  {isImage && (
                    <button type="button" onClick={() => setPreviewUrl(url)} className="bg-white/90 rounded-full p-1.5 hover:bg-white">
                      <Maximize2 className="h-3.5 w-3.5 text-slate-700" />
                    </button>
                  )}
                  {!file.isLegacy && isEditMode && (
                    <button
                      type="button"
                      onClick={() => handleDelete(file, idx)}
                      disabled={deleting === idx}
                      className="bg-red-500/90 rounded-full p-1.5 hover:bg-red-600 text-white"
                      data-testid={`delete-file-${category.key}-${idx}`}
                    >
                      {deleting === idx ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                    </button>
                  )}
                </div>

                {/* Compressed badge */}
                {file.compressed && (
                  <span className="absolute top-1 left-1 bg-green-500 text-white text-[9px] px-1.5 py-0.5 rounded-full">Compressed</span>
                )}

                {/* Size */}
                {file.size > 0 && (
                  <span className="absolute bottom-1 right-1 bg-black/60 text-white text-[9px] px-1 py-0.5 rounded">{formatFileSize(file.size)}</span>
                )}
              </div>
            );
          })}

          {/* Pending files (create mode) */}
          {(pendingFiles || []).map((file, idx) => (
            <div key={`pending-${idx}`} className="relative group aspect-square rounded-lg overflow-hidden border-2 border-dashed border-[#c9a227]/50 bg-[#fffdf5]">
              {file.type?.startsWith("image/") ? (
                <img src={URL.createObjectURL(file)} alt={file.name} className="w-full h-full object-cover" />
              ) : file.type?.startsWith("video/") ? (
                <div className="w-full h-full flex items-center justify-center bg-slate-900">
                  <Video className="h-8 w-8 text-white/50" />
                </div>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center p-2">
                  <FileImage className="h-8 w-8 text-[#c9a227]" />
                  <span className="text-[10px] text-slate-500 mt-1 truncate max-w-full">{file.name}</span>
                </div>
              )}
              <button
                type="button"
                onClick={() => onPendingRemove(category.key, idx)}
                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="h-3 w-3" />
              </button>
              <span className="absolute bottom-1 right-1 bg-amber-500/80 text-white text-[9px] px-1 py-0.5 rounded">Pending</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-6 text-center border-t border-dashed border-slate-200">
          <FolderUp className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-400 mb-1">Drag & drop files here</p>
          <p className="text-xs text-slate-300">or click "Add Files" above</p>
        </div>
      )}

      {/* Drag Overlay */}
      {dragOver && (
        <div className="p-4 text-center bg-[#c9a227]/10 border-t border-[#c9a227]/30">
          <Upload className="h-6 w-6 text-[#c9a227] mx-auto mb-1 animate-bounce" />
          <p className="text-sm font-medium text-[#c9a227]">Drop files to upload to {category.label}</p>
        </div>
      )}

      {/* Fullscreen Preview Modal */}
      {previewUrl && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setPreviewUrl(null)}>
          <img src={previewUrl} alt="Preview" className="max-w-full max-h-full object-contain rounded-lg" />
          <button onClick={() => setPreviewUrl(null)} className="absolute top-4 right-4 bg-white rounded-full p-2">
            <X className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
}

/** External Links Manager */
function ExternalLinksSection({ propertyId, links, onLinkAdded, onLinkRemoved, isEditMode }) {
  const [newUrl, setNewUrl] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [adding, setAdding] = useState(false);
  const token = localStorage.getItem("token");

  const handleAddLink = async () => {
    if (!newUrl.trim()) return;
    if (isEditMode && propertyId) {
      setAdding(true);
      try {
        const res = await axios.post(`${API}/properties/${propertyId}/external-link`,
          { url: newUrl, title: newTitle || newUrl, type: "other" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        onLinkAdded(res.data.link);
        toast.success("Link added");
        setNewUrl("");
        setNewTitle("");
      } catch (err) {
        toast.error("Failed to add link");
      } finally {
        setAdding(false);
      }
    } else {
      onLinkAdded({ id: Date.now().toString(), url: newUrl, title: newTitle || newUrl, type: "other" });
      setNewUrl("");
      setNewTitle("");
    }
  };

  const handleRemoveLink = async (linkId) => {
    if (isEditMode && propertyId) {
      try {
        await axios.delete(`${API}/properties/${propertyId}/external-link/${linkId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        onLinkRemoved(linkId);
        toast.success("Link removed");
      } catch (err) {
        toast.error("Failed to remove link");
      }
    } else {
      onLinkRemoved(linkId);
    }
  };

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="external-links-section">
      <div className="bg-gradient-to-r from-slate-50 to-[#f8f6f0] px-4 py-3 flex items-center gap-2">
        <Link2 className="h-5 w-5 text-[#0f2744]" />
        <div>
          <h4 className="text-sm font-semibold text-[#0f2744]">External Links</h4>
          <p className="text-xs text-slate-500">Add virtual tour, website, or other links</p>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {/* Existing links */}
        {(links || []).map((link) => (
          <div key={link.id} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
            <ExternalLink className="h-4 w-4 text-blue-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-700 truncate">{link.title}</p>
              <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline truncate block">{link.url}</a>
            </div>
            <button type="button" onClick={() => handleRemoveLink(link.id)} className="text-red-400 hover:text-red-600 flex-shrink-0">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}

        {/* Add new link */}
        <div className="flex gap-2">
          <Input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Link title (optional)"
            className="h-9 text-sm flex-1"
          />
          <Input
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            placeholder="https://..."
            className="h-9 text-sm flex-[2]"
            data-testid="external-link-url-input"
          />
          <Button
            type="button"
            size="sm"
            onClick={handleAddLink}
            disabled={!newUrl.trim() || adding}
            className="bg-[#c9a227] hover:bg-[#b8911f] text-white h-9 px-3"
            data-testid="add-external-link-btn"
          >
            {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** YouTube Section */
function YouTubeSection({ formData, onInputChange }) {
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="youtube-section">
      <div className="bg-gradient-to-r from-slate-50 to-[#f8f6f0] px-4 py-3 flex items-center gap-2">
        <Youtube className="h-5 w-5 text-red-600" />
        <div>
          <h4 className="text-sm font-semibold text-[#0f2744]">YouTube Video Link</h4>
          <p className="text-xs text-slate-500">Add a YouTube video tour of your property</p>
        </div>
        {formData.youtube_video_url && <CheckCircle className="h-4 w-4 text-green-500 ml-auto" />}
      </div>
      <div className="p-4">
        <div className="relative">
          <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            name="youtube_video_url"
            type="url"
            value={formData.youtube_video_url || ""}
            onChange={onInputChange}
            className="pl-10 h-10 rounded-lg"
            placeholder="https://youtube.com/watch?v=..."
            data-testid="youtube-url-input"
          />
        </div>
        {formData.youtube_video_url && (
          <div className="mt-3 rounded-lg overflow-hidden border border-slate-200">
            <iframe
              src={formData.youtube_video_url.replace("watch?v=", "embed/").replace("youtu.be/", "youtube.com/embed/")}
              className="w-full aspect-video"
              allowFullScreen
              title="Property Video"
            />
          </div>
        )}
      </div>
    </div>
  );
}

/** Bulk Upload Drop Zone - supports dragging many files at once */
function BulkDropZone({ propertyId, isEditMode, onFileUploaded }) {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState("");
  const bulkInputRef = useRef(null);
  const token = localStorage.getItem("token");

  const categorizeFile = (file) => {
    const name = file.name.toLowerCase();
    if (name.includes("floor") && !name.includes("room")) return "floor_plan";
    if (name.includes("project") || name.includes("master")) return "project_plan";
    if (name.includes("location") || name.includes("map")) return "location_map";
    if (name.includes("room")) return "room_floor_plan";
    if (name.includes("3d") || name.includes("render") || name.includes("perspective")) return "three_d_view";
    if (file.type?.startsWith("video/")) return "videos";
    return "images"; // Default: property images
  };

  const handleBulkUpload = async (files) => {
    if (!isEditMode || !propertyId || !files.length) return;
    setUploading(true);

    // Group by category
    const groups = {};
    for (const file of files) {
      const cat = categorizeFile(file);
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(file);
    }

    let totalUploaded = 0;
    for (const [category, catFiles] of Object.entries(groups)) {
      setProgress(`Uploading ${catFiles.length} file(s) to ${category.replace(/_/g, " ")}...`);
      const formData = new FormData();
      formData.append("category", category);
      catFiles.forEach(f => formData.append("files", f));

      try {
        const res = await axios.post(`${API}/properties/${propertyId}/media-upload`, formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
        const uploaded = res.data.files || [];
        totalUploaded += uploaded.length;
        onFileUploaded(category, uploaded);
      } catch (err) {
        toast.error(`Failed to upload to ${category}: ${err.response?.data?.detail || err.message}`);
      }
    }

    toast.success(`${totalUploaded} file(s) uploaded across ${Object.keys(groups).length} categories`);
    setUploading(false);
    setProgress("");
  };

  return (
    <div
      className={`border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${
        dragOver ? "border-[#c9a227] bg-[#fffdf5]" : "border-slate-300 hover:border-[#c9a227]/50"
      }`}
      data-testid="bulk-upload-zone"
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        handleBulkUpload(Array.from(e.dataTransfer.files));
      }}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
      onClick={() => !uploading && bulkInputRef.current?.click()}
    >
      <input
        ref={bulkInputRef}
        type="file"
        multiple
        accept="image/*,video/*,.pdf"
        onChange={(e) => {
          handleBulkUpload(Array.from(e.target.files));
          e.target.value = "";
        }}
        className="hidden"
        data-testid="bulk-upload-input"
      />
      {uploading ? (
        <>
          <Loader2 className="h-10 w-10 text-[#c9a227] mx-auto mb-2 animate-spin" />
          <p className="text-sm font-medium text-[#0f2744]">{progress}</p>
        </>
      ) : (
        <>
          <FolderUp className="h-10 w-10 text-[#c9a227] mx-auto mb-2" />
          <p className="text-sm font-semibold text-[#0f2744] mb-1">Bulk Upload - Drop Multiple Files</p>
          <p className="text-xs text-slate-500">
            Files are auto-sorted by name: "floor...", "project...", "room...", "3d...", "map..." etc.
            <br />Remaining files go to Property Images. Videos auto-detected.
          </p>
        </>
      )}
    </div>
  );
}

/** Media Templates Section */
function MediaTemplatesSection({ propertyId, isEditMode, mediaState, onMediaStateUpdate }) {
  const [templates, setTemplates] = useState([]);
  const [showSave, setShowSave] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateDesc, setTemplateDesc] = useState("");
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(null);
  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user.role === "admin";

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await axios.get(`${API}/media-templates`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTemplates(res.data || []);
      } catch { /* ignore */ }
    };
    fetchTemplates();
  }, [token]);

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) return;
    setSaving(true);
    try {
      const templateData = {
        name: templateName,
        description: templateDesc,
        links: mediaState.external_links || [],
      };
      const res = await axios.post(`${API}/media-templates`, templateData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      setTemplates(prev => [...prev, res.data]);
      toast.success(`Template "${templateName}" saved`);
      setTemplateName("");
      setTemplateDesc("");
      setShowSave(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleApplyTemplate = async (template) => {
    if (!isEditMode || !propertyId) {
      // For create mode, apply links locally
      const links = template.links || [];
      links.forEach(link => {
        onMediaStateUpdate("external_links", [...(mediaState.external_links || []), { id: Date.now().toString(), ...link }]);
      });
      toast.success(`Template "${template.name}" applied (links added)`);
      return;
    }

    setApplying(template.id);
    try {
      await axios.post(`${API}/properties/${propertyId}/apply-media-template/${template.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Refresh property data
      const propRes = await axios.get(`${API}/properties/${propertyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onMediaStateUpdate("external_links", propRes.data.external_links || []);
      toast.success(`Template "${template.name}" applied`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to apply template");
    } finally {
      setApplying(null);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await axios.delete(`${API}/media-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTemplates(prev => prev.filter(t => t.id !== templateId));
      toast.success("Template deleted");
    } catch (err) {
      toast.error("Failed to delete template");
    }
  };

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="media-templates-section">
      <div className="bg-gradient-to-r from-slate-50 to-[#f8f6f0] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bookmark className="h-5 w-5 text-[#0f2744]" />
          <div>
            <h4 className="text-sm font-semibold text-[#0f2744]">Media Templates</h4>
            <p className="text-xs text-slate-500">Save & reuse link sets across properties</p>
          </div>
        </div>
        {isAdmin && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setShowSave(!showSave)}
            className="text-xs gap-1"
            data-testid="save-template-toggle"
          >
            <Save className="h-3.5 w-3.5" />
            Save Current as Template
          </Button>
        )}
      </div>

      <div className="p-4 space-y-3">
        {/* Save Template Form */}
        {showSave && isAdmin && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2" data-testid="save-template-form">
            <Input
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Template name (e.g. Standard 2BHK)"
              className="h-8 text-sm"
              data-testid="template-name-input"
            />
            <Input
              value={templateDesc}
              onChange={(e) => setTemplateDesc(e.target.value)}
              placeholder="Description (optional)"
              className="h-8 text-sm"
            />
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                onClick={handleSaveTemplate}
                disabled={!templateName.trim() || saving}
                className="bg-[#c9a227] hover:bg-[#b8911f] text-white text-xs"
                data-testid="save-template-btn"
              >
                {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                Save Template
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => setShowSave(false)} className="text-xs">
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Template List */}
        {templates.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {templates.map(t => (
              <div key={t.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 border border-slate-200" data-testid={`template-${t.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">{t.name}</p>
                  {t.description && <p className="text-xs text-slate-400 truncate">{t.description}</p>}
                  <p className="text-[10px] text-slate-400">{(t.links || []).length} link(s)</p>
                </div>
                <div className="flex items-center gap-1 ml-2">
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => handleApplyTemplate(t)}
                    disabled={applying === t.id}
                    className="bg-[#0f2744] hover:bg-[#1a3a5c] text-white text-xs h-7 px-2"
                    data-testid={`apply-template-${t.id}`}
                  >
                    {applying === t.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Apply"}
                  </Button>
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => handleDeleteTemplate(t.id)}
                      className="text-red-400 hover:text-red-600 p-1"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-400 text-center py-2">No templates saved yet</p>
        )}
      </div>
    </div>
  );
}

/** Main Media Manager Tab */
export default function MediaManagerTab({
  formData,
  propertyId,
  isEditMode,
  mediaState,
  pendingMediaFiles,
  onInputChange,
  onMediaStateUpdate,
  onPendingFileAdd,
  onPendingFileRemove,
  onLinkAdded,
  onLinkRemoved,
}) {
  const handleFileUploaded = useCallback((categoryKey, uploadedFiles) => {
    const fieldName = FIELD_MAP[categoryKey];
    onMediaStateUpdate(fieldName, [
      ...(mediaState[fieldName] || []),
      ...uploadedFiles,
    ]);
  }, [mediaState, onMediaStateUpdate]);

  const handleFileDeleted = useCallback((categoryKey, storagePath) => {
    const fieldName = FIELD_MAP[categoryKey];
    onMediaStateUpdate(
      fieldName,
      (mediaState[fieldName] || []).filter(f => f.storage_path !== storagePath)
    );
  }, [mediaState, onMediaStateUpdate]);

  return (
    <div className="space-y-4">
      {/* Upload Guidelines */}
      <div className="bg-gradient-to-r from-[#0f2744] to-[#1a3a5c] rounded-lg p-4 text-white">
        <h4 className="font-semibold mb-2">Media Upload Guidelines</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <div className="bg-white/10 rounded p-2">
            <p className="font-medium">Images</p>
            <p className="text-white/70">JPG, PNG, WEBP</p>
          </div>
          <div className="bg-white/10 rounded p-2">
            <p className="font-medium">Videos</p>
            <p className="text-white/70">MP4, MOV, WEBM</p>
          </div>
          <div className="bg-white/10 rounded p-2">
            <p className="font-medium">Documents</p>
            <p className="text-white/70">PDF</p>
          </div>
          <div className="bg-white/10 rounded p-2">
            <p className="font-medium flex items-center gap-1">Auto-compress <CheckCircle className="h-3 w-3 text-green-400" /></p>
            <p className="text-white/70">Files &gt; 5MB auto-compressed</p>
          </div>
        </div>
      </div>

      {/* Media Categories */}
      <div className="space-y-3">
        {MEDIA_CATEGORIES.map((cat) => (
          <CategoryCard
            key={cat.key}
            category={cat}
            propertyId={propertyId}
            existingFiles={mediaState[FIELD_MAP[cat.key]] || []}
            legacyUrl={LEGACY_FIELD_MAP[cat.key] ? formData[LEGACY_FIELD_MAP[cat.key]] : null}
            pendingFiles={pendingMediaFiles?.[cat.key] || []}
            onPendingAdd={onPendingFileAdd}
            onPendingRemove={onPendingFileRemove}
            onFileUploaded={handleFileUploaded}
            onFileDeleted={handleFileDeleted}
            isEditMode={isEditMode}
          />
        ))}
      </div>

      {/* Bulk Upload Zone */}
      {isEditMode && propertyId && (
        <BulkDropZone
          propertyId={propertyId}
          isEditMode={isEditMode}
          onFileUploaded={handleFileUploaded}
        />
      )}

      {/* YouTube Section */}
      <YouTubeSection formData={formData} onInputChange={onInputChange} />

      {/* External Links */}
      <ExternalLinksSection
        propertyId={propertyId}
        links={mediaState.external_links || formData.external_links || []}
        onLinkAdded={onLinkAdded}
        onLinkRemoved={onLinkRemoved}
        isEditMode={isEditMode}
      />

      {/* Media Templates */}
      <MediaTemplatesSection
        propertyId={propertyId}
        isEditMode={isEditMode}
        mediaState={mediaState}
        onMediaStateUpdate={onMediaStateUpdate}
      />
    </div>
  );
}
